/**
 * Mock connectors — CRUD + test against a localStorage-persisted
 * list. Three demo connectors are seeded on first read so the page
 * is never empty for a new user.
 *
 * The mock `test` endpoint always returns success with a small
 * latency. The real engine backend will replace this with an actual
 * reach-out to the configured endpoint; the UI contract stays the
 * same (`ConnectorTestResult { ok, message, latency_ms }`).
 */

import type {
  Connector,
  ConnectorCreateInput,
  ConnectorTestResult,
  ConnectorType,
  ConnectorUpdateInput,
  EntityId,
} from '../types';

const STORAGE_KEY = 'gi-engine.connectors.v1';
const MOCK_LATENCY_MS = 160;
const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

interface PersistedConnectors {
  version: 1;
  connectors: Connector[];
}

function emptyState(): PersistedConnectors {
  return { version: 1, connectors: [] };
}

function load(): PersistedConnectors {
  if (typeof localStorage === 'undefined') return emptyState();
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return emptyState();
  try {
    const parsed = JSON.parse(raw) as PersistedConnectors;
    if (parsed.version !== 1) return emptyState();
    return parsed;
  } catch {
    return emptyState();
  }
}

function save(state: PersistedConnectors): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

// ─── Public surface ───────────────────────────────────────────────

export async function listConnectors(): Promise<Connector[]> {
  await sleep(MOCK_LATENCY_MS);
  let state = load();
  if (state.connectors.length === 0) {
    state = { version: 1, connectors: seedDemoConnectors() };
    save(state);
  }
  // Newest first — matches how vault / marketplace sort.
  return [...state.connectors].sort((a, b) => b.created_at.localeCompare(a.created_at));
}

export async function getConnector(id: string): Promise<Connector | null> {
  await sleep(MOCK_LATENCY_MS);
  return load().connectors.find((c) => c.id === id) ?? null;
}

export async function createConnector(input: ConnectorCreateInput): Promise<Connector> {
  await sleep(MOCK_LATENCY_MS);
  const state = load();
  const connector: Connector = {
    id: `conn_${randomId()}`,
    name: input.name,
    type: input.type,
    config: input.config,
    status: 'active',
    linked_gi_ids: input.linked_gi_ids,
    created_at: new Date().toISOString(),
    last_tested_at: null,
    last_message: null,
  };
  state.connectors.push(connector);
  save(state);
  return connector;
}

export async function updateConnector(input: ConnectorUpdateInput): Promise<Connector> {
  await sleep(MOCK_LATENCY_MS);
  const state = load();
  const idx = state.connectors.findIndex((c) => c.id === input.id);
  if (idx === -1) {
    throw new Error(`connector not found: ${input.id}`);
  }
  const prev = state.connectors[idx]!;
  const updated: Connector = {
    ...prev,
    name: input.name,
    config: input.config,
    status: input.status,
    linked_gi_ids: input.linked_gi_ids,
  };
  state.connectors[idx] = updated;
  save(state);
  return updated;
}

export async function deleteConnector(id: string): Promise<void> {
  await sleep(MOCK_LATENCY_MS);
  const state = load();
  state.connectors = state.connectors.filter((c) => c.id !== id);
  save(state);
}

export async function testConnector(id: string): Promise<ConnectorTestResult> {
  // Simulate a round-trip. Real backend will actually reach out.
  const latency = 220 + Math.floor(Math.random() * 320);
  await sleep(latency);
  const state = load();
  const idx = state.connectors.findIndex((c) => c.id === id);
  if (idx === -1) {
    return { ok: false, message: 'Connector not found.', latency_ms: latency };
  }
  const result: ConnectorTestResult = {
    ok: true,
    message: `Reached endpoint in ${latency} ms.`,
    latency_ms: latency,
  };
  const prev = state.connectors[idx]!;
  state.connectors[idx] = {
    ...prev,
    status: 'active',
    last_tested_at: new Date().toISOString(),
    last_message: result.message,
  };
  save(state);
  return result;
}

export async function resetConnectors(): Promise<void> {
  await sleep(MOCK_LATENCY_MS);
  save({ version: 1, connectors: seedDemoConnectors() });
}

// ─── Demo seed ────────────────────────────────────────────────────
//
// Three pre-filled connectors that let a new user immediately see
// the shape of the feature without filling anything in.

function seedDemoConnectors(): Connector[] {
  const now = Date.now();
  const iso = (offsetMs: number) => new Date(now - offsetMs).toISOString();

  return [
    {
      id: 'conn_demo_stripe',
      name: 'Stripe webhook',
      type: 'webhook',
      config: {
        endpoint: 'https://api.example.com/webhooks/stripe',
        auth: 'bearer',
        headers: ['x-env=production', 'x-source=stripe'],
        notes: 'Receives payment.succeeded events, forwards them into the transaction-log GI.',
      },
      status: 'active',
      linked_gi_ids: [],
      created_at: iso(1000 * 60 * 60 * 24 * 4),
      last_tested_at: iso(1000 * 60 * 60 * 2),
      last_message: 'Reached endpoint in 312 ms.',
    },
    {
      id: 'conn_demo_s3',
      name: 'Archive bucket',
      type: 'file_storage',
      config: {
        endpoint: 's3://gi-engine-archive/daily/',
        auth: 'api_key',
        headers: [],
        notes: 'Nightly dump of signed audit packs, retained 7 years.',
      },
      status: 'active',
      linked_gi_ids: [],
      created_at: iso(1000 * 60 * 60 * 24 * 11),
      last_tested_at: iso(1000 * 60 * 60 * 24 * 1),
      last_message: 'Reached endpoint in 198 ms.',
    },
    {
      id: 'conn_demo_smtp',
      name: 'Regulator alerts',
      type: 'email',
      config: {
        endpoint: 'smtp.office365.example.com:587',
        auth: 'basic',
        headers: [],
        notes: 'Emails flagged transactions to the finance team every weekday at 09:00.',
      },
      status: 'disabled',
      linked_gi_ids: [],
      created_at: iso(1000 * 60 * 60 * 24 * 30),
      last_tested_at: null,
      last_message: null,
    },
  ];
}

function randomId(): string {
  // 12-hex. Enough uniqueness for the mock; real engine assigns its
  // own identifier on create.
  const bytes = new Uint8Array(6);
  if (typeof crypto !== 'undefined') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

// ─── Convenience filter — used by the Vault "chips on card" lookup ──

export async function listConnectorsForGi(root_id: EntityId): Promise<Connector[]> {
  const all = await listConnectors();
  return all.filter((c) => c.linked_gi_ids.includes(root_id));
}

export type { ConnectorType };
